import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { api } from '@/lib/api';
import { errToToast } from '@/lib/humanizeApiError';
import i18n from '@/i18n';
import type { DmFolder } from '@/types/api';

/**
 * Telegram-style DM folders for the sidebar. A single React Query holds the
 * snapshot; mutations invalidate it. There's no socket fan-out server-side
 * (the only consumer is the same user's other tabs), so we just refetch.
 *
 * Server contract (server/src/routes/dmFolders.ts):
 *   GET    /api/me/dm-folders                          → { folders }
 *   POST   /api/me/dm-folders                { name }  → create (appended)
 *   PATCH  /api/me/dm-folders/:id            { name?, position? }
 *   DELETE /api/me/dm-folders/:id
 *   PUT    /api/me/dm-folders/:id/entries/:channelId   → add a DM
 *   DELETE /api/me/dm-folders/:id/entries/:channelId   → remove it
 */

export const DM_FOLDERS_QUERY_KEY = ['me', 'dm-folders'] as const;

/**
 * Narrow a sidebar-ordered DM list down to a folder's members, reordered by
 * the folder's own entry positions. Ids whose channel is no longer present
 * (left/deleted DM) are dropped. Pure — unit-tested separately.
 */
export function orderChannelsByFolder<T extends { id: string }>(
  channels: T[],
  folderChannelIds: string[],
): T[] {
  const byId = new Map(channels.map((c) => [c.id, c]));
  return folderChannelIds
    .map((id) => byId.get(id))
    .filter((c): c is T => c !== undefined);
}

/**
 * Telegram-style folder badge: how many of the given channels have unread
 * messages. Counts *chats* with unread, not total messages — matching the
 * badge semantics on Telegram's folder tabs. Channels absent from the unread
 * map (or with 0) don't count.
 */
export function countUnreadChats(
  channelIds: string[],
  unreadByChannel: Record<string, number>,
): number {
  return channelIds.reduce(
    (n, id) => n + ((unreadByChannel[id] ?? 0) > 0 ? 1 : 0),
    0,
  );
}

export interface UseDmFolders {
  folders: DmFolder[];
  isLoading: boolean;
  isError: boolean;
  /** Ordered channel ids that belong to at least one folder. */
  foldersOf: (channelId: string) => DmFolder[];
  /** Resolves with the freshly-created folder (so callers can add an entry). */
  createFolder: (name: string) => Promise<DmFolder>;
  renameFolder: (id: string, name: string) => void;
  deleteFolder: (id: string) => void;
  /** Toggle a DM channel's membership in a folder. */
  toggleEntry: (folderId: string, channelId: string, member: boolean) => void;
  /** True while any folder mutation is in flight (disables UI). */
  isMutating: boolean;
}

export function useDmFolders(): UseDmFolders {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: DM_FOLDERS_QUERY_KEY,
    queryFn: () => api.listDmFolders(),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: DM_FOLDERS_QUERY_KEY });
  };

  const createMutation = useMutation({
    mutationFn: (name: string) => api.createDmFolder(name),
    onSuccess: invalidate,
    onError: (err) => toast.error(...errToToast(err, i18n.t('lib2.dmFolders.createFailed'))),
  });
  const renameMutation = useMutation({
    mutationFn: (vars: { id: string; name: string }) =>
      api.updateDmFolder(vars.id, { name: vars.name }),
    onSuccess: invalidate,
    onError: (err) => toast.error(...errToToast(err, i18n.t('lib2.dmFolders.renameFailed'))),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteDmFolder(id),
    onSuccess: invalidate,
    onError: (err) => toast.error(...errToToast(err, i18n.t('lib2.dmFolders.deleteFailed'))),
  });
  const entryMutation = useMutation({
    mutationFn: (vars: { folderId: string; channelId: string; member: boolean }) =>
      vars.member
        ? api.addDmFolderEntry(vars.folderId, vars.channelId)
        : api.removeDmFolderEntry(vars.folderId, vars.channelId),
    onSuccess: invalidate,
    onError: (err) => toast.error(...errToToast(err, i18n.t('lib2.dmFolders.updateFailed'))),
  });

  const folders = query.data?.folders ?? [];

  const foldersOf = (channelId: string): DmFolder[] =>
    folders.filter((f) => f.channelIds.includes(channelId));

  return {
    folders,
    isLoading: query.isLoading,
    isError: query.isError,
    foldersOf,
    createFolder: (name: string) => createMutation.mutateAsync(name),
    renameFolder: (id: string, name: string) => renameMutation.mutate({ id, name }),
    deleteFolder: (id: string) => deleteMutation.mutate(id),
    toggleEntry: (folderId: string, channelId: string, member: boolean) =>
      entryMutation.mutate({ folderId, channelId, member }),
    isMutating:
      createMutation.isPending ||
      renameMutation.isPending ||
      deleteMutation.isPending ||
      entryMutation.isPending,
  };
}
